import React, { memo, useCallback, useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import '../fichierCss/Actualites.css';
import RessourceApi from '../api/RessourceApi';
import Ressource from '../modeles/Ressource';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import ListeEvenements from '../composants/ListeEvenements';
import ImageGenerique from '../composants/ImageGenerique';
import DescriptionGenerique from '../composants/DescriptionGenerique';
import Don from '../composants/Don';
import '../fichierCss/Accueil.css';
import Adresse from '../composants/Adresse';
import presidente from '../images/navigation/presidente.jpg';
import { obtenirImageDepuisRessource } from '../utils/ressourceImageUtils';
import IMAGES_EVENEMENTS from '../constantes/evenements/ImagesEvenements';

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(2, 1),
    marginTop: theme.spacing(1.5),
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(3, 1),
      marginTop: theme.spacing(2),
    },
  },
  topGrid: {
    minHeight: 620,
    [theme.breakpoints.down('md')]: { minHeight: 'auto' },
  },
  card: {
    borderRadius: 8,
    overflow: 'hidden',
    boxShadow: '0 10px 24px rgba(7, 33, 52, 0.08)',
    padding: theme.spacing(2),
    height: '100%',
    background: 'linear-gradient(180deg, #ffffff 0%, #f8fcff 100%)',
    border: '1px solid rgba(21, 76, 105, 0.12)',
  },
  sectionTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(1.5),
    color: '#0f2d3f',
    letterSpacing: '0.01em',
  },
  mediaShell: {
    width: '100%',
    minHeight: 260,
    maxHeight: 420,
    overflow: 'hidden',
    marginBottom: theme.spacing(1.5),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    background: '#f1f7fb',
    border: '1px solid rgba(15, 45, 63, 0.1)',
  },
  mediaInner: {
    width: '100%',
    height: '100%',
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  mediaTitle: {
    marginTop: theme.spacing(1.25),
    marginBottom: theme.spacing(0.5),
    fontWeight: 700,
    fontSize: '1.35rem',
    color: '#12354a',
    textAlign: 'center',
    width: '100%',
  },
  descriptionWrap: {
    marginTop: theme.spacing(1.2),
    padding: theme.spacing(1.5, 1.25),
    borderRadius: 8,
    background: '#ffffff',
    border: '1px solid rgba(15, 45, 63, 0.1)',
    maxHeight: 230,
    overflowY: 'auto' as 'auto',
  },
  eventsWrap: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto' as 'auto',
    maxHeight: 500,
    paddingRight: theme.spacing(0.4),
  },
}));

const PresentationContainer = memo(() => {
  const [evenements, setEvenements] = useState<Ressource[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [imageChoisie, setImageChoisie] = useState<string>(presidente);
  const [titreAffiche, setTitreAffiche] = useState('');
  const [description, setDescription] = useState('');
  const classes = useStyles();

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      try {
        const [presData, evData] = await Promise.all([
          RessourceApi.obtenirRessourcesParCategorie('Presentation'),
          RessourceApi.obtenirRessourcesParCategorie('Evenement'),
        ]);
        if (!isMounted) return;
        const pres = Array.isArray(presData) ? presData[0] : null;
        if (pres) {
          setImageChoisie(obtenirImageDepuisRessource(pres.cheminFichier, 'Presentation', presidente));
          setTitreAffiche(pres.titre || '');
          setDescription(pres.description || '');
        }
        const evts = Array.isArray(evData) ? evData : [];
        setEvenements(evts);
        if (!pres && evts.length > 0) {
          const premier = evts[0];
          const localFallback = IMAGES_EVENEMENTS[0] || '';
          const img = obtenirImageDepuisRessource(premier.cheminFichier, 'Evenement', localFallback, 0) || localFallback || presidente;
          setImageChoisie(img);
          setDescription(premier.description || premier.resume || '');
          setTitreAffiche(premier.titre || '');
        }
      } catch {
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };
    fetchData();
    return () => { isMounted = false; };
  }, []);

  const handleListItemClick = useCallback((event: React.MouseEvent, index: number) => {
    setSelectedIndex(index);
  }, []);

  const handleEvenementClick = useCallback((evenement: Ressource) => {
    const imageUrl = obtenirImageDepuisRessource(evenement.cheminFichier, 'Evenement', '');
    setImageChoisie(imageUrl || presidente);
    setDescription(evenement.description || evenement.resume || '');
    setTitreAffiche(evenement.titre || '');
  }, []);

  return (
    <div className={classes.root}>
      <Grid container spacing={3} alignItems="stretch" className={classes.topGrid}>
        <Grid item xs={12} lg={8} style={{ display: 'flex', flexDirection: 'column' }}>
          <Paper className={classes.card} style={{ height: '100%', minHeight: 620, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div className={classes.mediaShell}>
              <div className={classes.mediaInner}>
                <ImageGenerique key={imageChoisie} image={imageChoisie} titre={titreAffiche} fallbackImage={presidente} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
            </div>
            <div className={classes.mediaTitle}>{titreAffiche}</div>
            <Divider style={{ margin: '8px 0' }} />
            <div className={classes.descriptionWrap}>
              <DescriptionGenerique description={description} />
            </div>
          </Paper>
        </Grid>
        <Grid item xs={12} lg={4} style={{ display: 'flex', flexDirection: 'column' }}>
          <Paper className={classes.card} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div className={classes.eventsWrap}>
              <ListeEvenements
                evenements={evenements}
                isLoading={isLoading}
                selectedIndex={selectedIndex}
                handleListItemClick={handleListItemClick}
                onEvenementClick={handleEvenementClick}
              />
            </div>
          </Paper>
        </Grid>
      </Grid>
      <Grid container spacing={3} style={{ marginTop: 24, minHeight: 180 }} alignItems="stretch">
        <Grid item xs={12} md={6} style={{ display: 'flex' }}>
          <Paper className={classes.card} style={{ minHeight: 180, height: '100%', width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <h2 className={classes.sectionTitle}>Faire un don</h2>
            <Divider style={{ marginBottom: '12px' }} />
            <Don />
          </Paper>
        </Grid>
        <Grid item xs={12} md={6} style={{ display: 'flex' }}>
          <Paper className={classes.card} id="map" style={{ minHeight: 180, height: '100%', width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <h2 className={classes.sectionTitle}>Adresse</h2>
            <Divider style={{ marginBottom: '12px' }} />
            <Adresse />
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
});

export default PresentationContainer;